import { Delete as DeleteIcon } from "@mui/icons-material";
import { FormControl, IconButton, MenuItem, TextField } from "@mui/material";
import PropTypes from "prop-types";
import React, { useCallback, useMemo } from "react";
import ROMDropdown from "./ROMDropdown";
import SearchableSelect from "./SearchableSelect";
import { SharedLineItemsTable } from "./SharedLineItemsTable";
import {
  budgetROMCellPropTypes,
  costROMCellPropTypes,
  revenueROMCellPropTypes,
} from "./sharedPropTypes";

const toInput = (v) => (v === null || v === undefined ? "" : String(v));
const toNum = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));

// Extracted Cell components to avoid nesting
const ActionsCell = ({ row, onRemove }) => (
  <IconButton size="small" onClick={() => onRemove(row.original.id)} color="error">
    <DeleteIcon fontSize="small" />
  </IconButton>
);

ActionsCell.propTypes = {
  row: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const BudgetCodeCell = ({ row, onEdit, getDisplayText, dropdownOptions }) => (
  <FormControl size="small" sx={{ minWidth: 220, zIndex: 10 }}>
    <SearchableSelect
      value={row.original.budgetCodeId || ""}
      displayEmpty
      onChange={(e) => onEdit(row.original.id, "budgetCodeId", e.target.value)}
      renderValue={(v) =>
        v === "" ? (
          <span style={{ color: "#9e9e9e" }}>Select Budget Code</span>
        ) : (
          getDisplayText(v, dropdownOptions?.budgetCodes, "budgetCode")
        )
      }
      MenuProps={{
        PaperProps: {
          sx: {
            zIndex: 1300,
          },
        },
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left",
        },
      }}
    >
      <MenuItem value="" disabled>
        <em>Select Budget Code</em>
      </MenuItem>
      {dropdownOptions?.budgetCodes?.map((b) => (
        <MenuItem key={b.id} value={b.id}>
          {b.code ? `${b.code} — ${b.name}` : b.name}
        </MenuItem>
      ))}
    </SearchableSelect>
  </FormControl>
);

BudgetCodeCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      budgetCodeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  onEdit: PropTypes.func,
  getDisplayText: PropTypes.func,
  dropdownOptions: PropTypes.shape({
    budgetCodes: PropTypes.arrayOf(PropTypes.object),
  }),
};

const DescriptionCell = ({ row, onEdit }) => (
  <TextField
    value={toInput(row.original.description)}
    onChange={(e) => onEdit(row.original.id, "description", e.target.value)}
    size="small"
    fullWidth
  />
);

DescriptionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      description: PropTypes.string,
    }),
  }),
  onEdit: PropTypes.func,
};

const UnitOfMeasureCell = ({ row, onEdit, getDisplayText, dropdownOptions }) => (
  <FormControl size="small" sx={{ minWidth: 120 }}>
    <SearchableSelect
      value={row.original.unitOfMeasure || ""}
      displayEmpty
      onChange={(e) => onEdit(row.original.id, "unitOfMeasure", e.target.value)}
      renderValue={(v) =>
        v === "" ? (
          <span style={{ color: "#9e9e9e" }}>Select UoM</span>
        ) : (
          getDisplayText(v, dropdownOptions?.uom, "unitOfMeasure")
        )
      }
    >
      <MenuItem value="" disabled>
        <em>Select UoM</em>
      </MenuItem>
      {dropdownOptions?.uom?.map((u) => (
        <MenuItem key={u.id || u.value} value={u.abbreviation || u.value}>
          {u.name}
        </MenuItem>
      ))}
    </SearchableSelect>
  </FormControl>
);

UnitOfMeasureCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      unitOfMeasure: PropTypes.string,
    }),
  }),
  onEdit: PropTypes.func,
  getDisplayText: PropTypes.func,
  dropdownOptions: PropTypes.shape({
    uom: PropTypes.arrayOf(PropTypes.object),
  }),
};

const VendorCell = ({ row, onEdit, getDisplayText, dropdownOptions }) => (
  <FormControl size="small" sx={{ minWidth: 200 }}>
    <SearchableSelect
      value={row.original.vendorId || ""}
      displayEmpty
      onChange={(e) => onEdit(row.original.id, "vendorId", e.target.value)}
      renderValue={(v) =>
        v === "" ? (
          <span style={{ color: "#9e9e9e" }}>Select Vendor</span>
        ) : (
          getDisplayText(v, dropdownOptions?.vendors, "vendorId")
        )
      }
    >
      <MenuItem value="" disabled>
        <em>Select Vendor</em>
      </MenuItem>
      {dropdownOptions?.vendors?.map((v) => (
        <MenuItem key={v.id} value={v.id}>
          {v.company}
        </MenuItem>
      ))}
    </SearchableSelect>
  </FormControl>
);

VendorCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      vendorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  onEdit: PropTypes.func,
  getDisplayText: PropTypes.func,
  dropdownOptions: PropTypes.shape({
    vendors: PropTypes.arrayOf(PropTypes.object),
  }),
};

const ContractCell = ({ row, onEdit, getDisplayText, dropdownOptions }) => (
  <FormControl size="small" sx={{ minWidth: 220 }}>
    <SearchableSelect
      value={row.original.contractId || ""}
      displayEmpty
      onChange={(e) => onEdit(row.original.id, "contractId", e.target.value)}
      renderValue={(v) =>
        v === "" ? (
          <span style={{ color: "#9e9e9e" }}>Select Contract</span>
        ) : (
          getDisplayText(v, dropdownOptions?.contracts, "contractId")
        )
      }
    >
      <MenuItem value="" disabled>
        <em>Select Contract</em>
      </MenuItem>
      {dropdownOptions?.contracts?.map((c) => (
        <MenuItem key={c.id} value={c.id}>
          {c.number ? `${c.number} — ${c.title}` : c.title}
        </MenuItem>
      ))}
    </SearchableSelect>
  </FormControl>
);

ContractCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      contractId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  onEdit: PropTypes.func,
  getDisplayText: PropTypes.func,
  dropdownOptions: PropTypes.shape({
    contracts: PropTypes.arrayOf(PropTypes.object),
  }),
};

const RevenueQtyCell = ({ row, onDec2Change }) => (
  <TextField
    type="text"
    inputMode="decimal"
    value={toInput(row.original.revenueQty)}
    onChange={onDec2Change(row.original.id, "revenueQty")}
    size="small"
    fullWidth
    inputProps={{
      step: "0.01",
      min: 0,
      pattern: String.raw`\d*(\.\d{0,})?`,
      style: { textAlign: "right" },
    }}
  />
);

RevenueQtyCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      revenueQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  onDec2Change: PropTypes.func,
};

const RevenueUnitCostCell = ({ row, onDec2Change }) => (
  <TextField
    type="text"
    inputMode="decimal"
    value={toInput(row.original.revenueUnitCost)}
    onChange={onDec2Change(row.original.id, "revenueUnitCost")}
    size="small"
    fullWidth
    inputProps={{
      step: "0.01",
      min: 0,
      pattern: String.raw`\d*(\.\d{0,})?`,
      style: { textAlign: "right" },
    }}
  />
);

RevenueUnitCostCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      revenueUnitCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  onDec2Change: PropTypes.func,
};

const RevenueROMCell = ({ row, onROMChange, onEdit }) => (
  <ROMDropdown
    value={row.original.revenueROM || null}
    onChange={(val) => onROMChange(row.original.id, "revenueROM", val)}
    revenueQty={toNum(row.original.revenueQty)}
    revenueUnitCost={toNum(row.original.revenueUnitCost)}
    costQty={toNum(row.original.costQty)}
    costUnitCost={toNum(row.original.costUnitCost)}
    budgetQty={toNum(row.original.budgetQty)}
    budgetUnitCost={toNum(row.original.budgetUnitCost)}
    type="revenue"
    onQuantityChange={(idx, field, value) => onEdit(idx, field, value)}
    onUnitCostChange={(idx, field, value) => onEdit(idx, field, value)}
    rowIndex={row.original.id}
  />
);

RevenueROMCell.propTypes = revenueROMCellPropTypes;

const CostQtyCell = ({ row, onDec2Change }) => (
  <TextField
    type="text"
    inputMode="decimal"
    value={toInput(row.original.costQty)}
    onChange={onDec2Change(row.original.id, "costQty")}
    size="small"
    fullWidth
    inputProps={{
      step: "0.01",
      min: 0,
      pattern: String.raw`\d*(\.\d{0,})?`,
      style: { textAlign: "right" },
    }}
  />
);

CostQtyCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      costQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  onDec2Change: PropTypes.func,
};

const CostUnitCostCell = ({ row, onDec2Change }) => (
  <TextField
    type="text"
    inputMode="decimal"
    value={toInput(row.original.costUnitCost)}
    onChange={onDec2Change(row.original.id, "costUnitCost")}
    size="small"
    fullWidth
    inputProps={{
      step: "0.01",
      min: 0,
      pattern: String.raw`\d*(\.\d{0,})?`,
      style: { textAlign: "right" },
    }}
  />
);

CostUnitCostCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      costUnitCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  onDec2Change: PropTypes.func,
};

const BudgetQtyCell = ({ row, onDec2Change }) => (
  <TextField
    type="text"
    inputMode="decimal"
    value={toInput(row.original.budgetQty)}
    onChange={onDec2Change(row.original.id, "budgetQty")}
    size="small"
    fullWidth
    inputProps={{
      step: "0.01",
      min: 0,
      pattern: String.raw`\d*(\.\d{0,})?`,
      style: { textAlign: "right" },
    }}
  />
);

BudgetQtyCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      budgetQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  onDec2Change: PropTypes.func,
};

const BudgetUnitCostCell = ({ row, onDec2Change }) => (
  <TextField
    type="text"
    inputMode="decimal"
    value={toInput(row.original.budgetUnitCost)}
    onChange={onDec2Change(row.original.id, "budgetUnitCost")}
    size="small"
    fullWidth
    inputProps={{
      step: "0.01",
      min: 0,
      pattern: String.raw`\d*(\.\d{0,})?`,
      style: { textAlign: "right" },
    }}
  />
);

BudgetUnitCostCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      budgetUnitCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  onDec2Change: PropTypes.func,
};

const CostROMCell = ({ row, onROMChange, onEdit }) => (
  <ROMDropdown
    value={row.original.costROM || null}
    onChange={(val) => onROMChange(row.original.id, "costROM", val)}
    revenueQty={toNum(row.original.revenueQty)}
    revenueUnitCost={toNum(row.original.revenueUnitCost)}
    costQty={toNum(row.original.costQty)}
    costUnitCost={toNum(row.original.costUnitCost)}
    budgetQty={toNum(row.original.budgetQty)}
    budgetUnitCost={toNum(row.original.budgetUnitCost)}
    type="cost"
    onQuantityChange={(idx, field, value) => onEdit(idx, field, value)}
    onUnitCostChange={(idx, field, value) => onEdit(idx, field, value)}
    rowIndex={row.original.id}
  />
);

CostROMCell.propTypes = costROMCellPropTypes;

const BudgetROMCell = ({ row, onROMChange, onEdit }) => (
  <ROMDropdown
    value={row.original.budgetROM || null}
    onChange={(val) => onROMChange(row.original.id, "budgetROM", val)}
    revenueQty={toNum(row.original.revenueQty)}
    revenueUnitCost={toNum(row.original.revenueUnitCost)}
    costQty={toNum(row.original.costQty)}
    costUnitCost={toNum(row.original.costUnitCost)}
    budgetQty={toNum(row.original.budgetQty)}
    budgetUnitCost={toNum(row.original.budgetUnitCost)}
    type="budget"
    onQuantityChange={(idx, field, value) => onEdit(idx, field, value)}
    onUnitCostChange={(idx, field, value) => onEdit(idx, field, value)}
    rowIndex={row.original.id}
  />
);

BudgetROMCell.propTypes = budgetROMCellPropTypes;

const ChangeEventLineItemsTable = ({
  rows,
  onRowsChange,
  dropdownOptions,
  getDisplayText,
  handleROMChange,
  handleDec2Change,
  revenueInputsDisabled = false,
}) => {
  const handleEdit = useCallback(
    (rowId, field, value) => {
      const updatedRows = rows.map((row, idx) =>
        idx === rowId ? { ...row, [field]: value } : row
      );
      onRowsChange(updatedRows);
    },
    [rows, onRowsChange]
  );

  const handleAddRow = useCallback(() => {
    const DEFAULT_ROW = {
      budgetCodeId: "",
      description: "",
      vendorId: "",
      contractId: "",
      unitOfMeasure: "",
      revenueQty: "",
      revenueUnitCost: "",
      revenueROM: null,
      costQty: "",
      costUnitCost: "",
      costROM: { method: "quantity_cost" },
      budgetQty: "",
      budgetUnitCost: "",
      budgetROM: null,
    };
    onRowsChange([...rows, { ...DEFAULT_ROW }]);
  }, [rows, onRowsChange]);

  const handleRemoveRow = useCallback(
    (rowId) => {
      const updatedRows = rows.filter((_, idx) => idx !== rowId);
      onRowsChange(updatedRows);
    },
    [rows, onRowsChange]
  );

  const data = useMemo(
    () =>
      rows.map((row, idx) => ({
        id: idx,
        rowNumber: idx + 1,
        budgetCodeId: row.budgetCodeId || "",
        description: row.description || "",
        unitOfMeasure: row.unitOfMeasure || "",
        vendorId: row.vendorId || "",
        contractId: row.contractId || "",
        revenueQty: row.revenueQty || "",
        revenueUnitCost: row.revenueUnitCost || "",
        revenueROM: row.revenueROM || null,
        costQty: row.costQty || "",
        costUnitCost: row.costUnitCost || "",
        costROM: row.costROM || { method: "quantity_cost" },
        budgetQty: row.budgetQty || "",
        budgetUnitCost: row.budgetUnitCost || "",
        budgetROM: row.budgetROM || null,
        budgetCode:
          getDisplayText(row.budgetCodeId, dropdownOptions?.budgetCodes, "budgetCode") || "",
        vendor: getDisplayText(row.vendorId, dropdownOptions?.vendors, "vendorId") || "",
        contract: getDisplayText(row.contractId, dropdownOptions?.contracts, "contractId") || "",
      })),
    [rows, dropdownOptions, getDisplayText]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "actions",
        header: "",
        size: 60,
        enableGrouping: false,
        Cell: ({ row }) => <ActionsCell row={row} onRemove={handleRemoveRow} />,
      },
      {
        accessorKey: "rowNumber",
        header: "#",
        size: 50,
        enableGrouping: false,
      },
      {
        accessorKey: "budgetCodeId",
        header: "Budget Code",
        size: 220,
        Cell: ({ row }) => (
          <BudgetCodeCell
            row={row}
            onEdit={handleEdit}
            getDisplayText={getDisplayText}
            dropdownOptions={dropdownOptions}
          />
        ),
        muiTableBodyCellProps: {
          sx: {
            overflow: "visible",
            position: "relative",
            zIndex: 10,
          },
        },
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 200,
        Cell: ({ row }) => <DescriptionCell row={row} onEdit={handleEdit} />,
        muiTableBodyCellProps: {
          sx: {
            overflow: "visible",
            position: "relative",
            zIndex: 1,
          },
        },
      },
      {
        accessorKey: "unitOfMeasure",
        header: "UoM",
        size: 120,
        Cell: ({ row }) => (
          <UnitOfMeasureCell
            row={row}
            onEdit={handleEdit}
            getDisplayText={getDisplayText}
            dropdownOptions={dropdownOptions}
          />
        ),
      },
      {
        accessorKey: "vendorId",
        header: "Vendor",
        size: 200,
        Cell: ({ row }) => (
          <VendorCell
            row={row}
            onEdit={handleEdit}
            getDisplayText={getDisplayText}
            dropdownOptions={dropdownOptions}
          />
        ),
      },
      {
        accessorKey: "contractId",
        header: "Contract",
        size: 220,
        Cell: ({ row }) => (
          <ContractCell
            row={row}
            onEdit={handleEdit}
            getDisplayText={getDisplayText}
            dropdownOptions={dropdownOptions}
          />
        ),
      },
      {
        accessorKey: "revenueQty",
        header: "Revenue Qty",
        size: 120,
        Cell: ({ row }) => <RevenueQtyCell row={row} onDec2Change={handleDec2Change} />,
        muiTableBodyCellProps: { align: "right" },
        muiTableHeadCellProps: { align: "right" },
      },
      {
        accessorKey: "revenueUnitCost",
        header: "Revenue Unit Cost",
        size: 140,
        Cell: ({ row }) => <RevenueUnitCostCell row={row} onDec2Change={handleDec2Change} />,
        muiTableBodyCellProps: { align: "right" },
        muiTableHeadCellProps: { align: "right" },
      },
      {
        accessorKey: "revenueROM",
        header: "Revenue ROM",
        size: 120,
        Cell: ({ row }) => (
          <RevenueROMCell row={row} onROMChange={handleROMChange} onEdit={handleEdit} />
        ),
      },
      {
        accessorKey: "costQty",
        header: "Cost Qty",
        size: 120,
        Cell: ({ row }) => <CostQtyCell row={row} onDec2Change={handleDec2Change} />,
        muiTableBodyCellProps: { align: "right" },
        muiTableHeadCellProps: { align: "right" },
      },
      {
        accessorKey: "costUnitCost",
        header: "Cost Unit Cost",
        size: 140,
        Cell: ({ row }) => <CostUnitCostCell row={row} onDec2Change={handleDec2Change} />,
        muiTableBodyCellProps: { align: "right" },
        muiTableHeadCellProps: { align: "right" },
      },
      {
        accessorKey: "costROM",
        header: "Cost ROM",
        size: 120,
        Cell: ({ row }) => (
          <CostROMCell row={row} onROMChange={handleROMChange} onEdit={handleEdit} />
        ),
      },
      {
        accessorKey: "budgetQty",
        header: "Budget Qty",
        size: 120,
        Cell: ({ row }) => <BudgetQtyCell row={row} onDec2Change={handleDec2Change} />,
        muiTableBodyCellProps: { align: "right" },
        muiTableHeadCellProps: { align: "right" },
      },
      {
        accessorKey: "budgetUnitCost",
        header: "Budget Unit Cost",
        size: 140,
        Cell: ({ row }) => <BudgetUnitCostCell row={row} onDec2Change={handleDec2Change} />,
        muiTableBodyCellProps: { align: "right" },
        muiTableHeadCellProps: { align: "right" },
      },
      {
        accessorKey: "budgetROM",
        header: "Budget ROM",
        size: 120,
        Cell: ({ row }) => (
          <BudgetROMCell row={row} onROMChange={handleROMChange} onEdit={handleEdit} />
        ),
      },
      {
        accessorKey: "budgetCode",
        header: "Budget Code",
        enableGrouping: true,
      },
      {
        accessorKey: "vendor",
        header: "Vendor",
        enableGrouping: true,
      },
      {
        accessorKey: "contract",
        header: "Contract",
        enableGrouping: true,
      },
    ],
    [
      handleEdit,
      handleRemoveRow,
      handleDec2Change,
      handleROMChange,
      dropdownOptions,
      getDisplayText,
    ]
  );

  const groupingKeyLabels = {
    budgetCode: "Budget Code",
    vendor: "Vendor",
    contract: "Contract",
  };

  return (
    <SharedLineItemsTable
      columns={columns}
      data={data}
      onAddRow={handleAddRow}
      availableGroupingKeys={["budgetCode", "vendor", "contract"]}
      groupingKeyLabels={groupingKeyLabels}
    />
  );
};

ChangeEventLineItemsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRowsChange: PropTypes.func.isRequired,
  dropdownOptions: PropTypes.shape({
    budgetCodes: PropTypes.arrayOf(PropTypes.object),
    vendors: PropTypes.arrayOf(PropTypes.object),
    contracts: PropTypes.arrayOf(PropTypes.object),
    uom: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  getDisplayText: PropTypes.func.isRequired,
  handleROMChange: PropTypes.func.isRequired,
  handleDec2Change: PropTypes.func.isRequired,
  revenueInputsDisabled: PropTypes.bool,
};

export default ChangeEventLineItemsTable;
